/*
 * Mad Tea: send Australian visitors from the US store to the AU homepage.
 * The Liquid snippet loads this file and supplies window.MadTeaGeoRedirect.
 * No credentials or visitor IP addresses are stored by this script.
 */
(function initialiseMadTeaRedirect() {
  'use strict';

  // 1. Check that this feature is enabled and this page allows redirection.
  const config = window.MadTeaGeoRedirect;

  if (!config || !config.enabled || window.__madTeaGeoStarted) return;
  window.__madTeaGeoStarted = true;

  const currentUrl = new URL(window.location.href);
  const shopify = window.Shopify;
  const isUsStore = /^(www\.)?madtea\.com$/.test(currentUrl.hostname);
  const isThemeEditor = shopify && (shopify.designMode || shopify.visualPreviewMode);
  const isEmbeddedPage = window.self !== window.top;

  if (!isUsStore || isThemeEditor || isEmbeddedPage) return;

  // Test mode is our own explicit setting, not Shopify's deprecated theme.role.
  // Missing settings default to testing, so an incomplete setup cannot redirect
  // ordinary visitors. The Liquid loader keeps this true in the draft theme.
  const isTestMode = config.testMode !== false;
  const isPreview = isTestMode || currentUrl.searchParams.has('preview_theme_id');
  const isPreviewTest = currentUrl.searchParams.get('madtea_geo_test') === '1';
  const isKnownBot = /bot|crawler|spider|slurp|bingpreview|facebookexternalhit|lighthouse/i
    .test(navigator.userAgent);
  const isExcludedPage = /^\/(cart|checkout|checkouts|account|apps|challenge|password)(\/|$)/
    .test(currentUrl.pathname);

  if (isPreview && !isPreviewTest) return;
  if (isKnownBot || isExcludedPage) return;

  // 2. Remember this session's choice and prevent repeated redirects.
  // Preview tests use separate storage so they do not affect normal visits.
  const storagePrefix = isPreview ? 'madtea-geo-qa-v1:' : 'madtea-geo-v1:';

  function readSessionValue(key) {
    try {
      return sessionStorage.getItem(storagePrefix + key);
    } catch {
      return null; // Some browsers block storage. The script can still run.
    }
  }

  function writeSessionValue(key, value) {
    try {
      sessionStorage.setItem(storagePrefix + key, value);
    } catch {
      // Storage is optional; a blocked write must not break the storefront.
    }
  }

  if (currentUrl.searchParams.get('madtea_region') === 'us') {
    writeSessionValue('stay', '1');
  }

  const choseUsStore = readSessionValue('stay') === '1';
  const alreadyRedirected = readSessionValue('redirected') === '1';

  if (choseUsStore || alreadyRedirected) return;

  try {
    const referringUrl = new URL(document.referrer);
    const cameFromAuStore = /^(www\.)?madtea\.com\.au$/.test(referringUrl.hostname);

    if (cameFromAuStore) {
      writeSessionValue('stay', '1');
      return;
    }
  } catch {
    // A visit without a referrer is normal, so continue the country check.
  }

  // 3. Stop redirection if the visitor starts using the page during the lookup.
  let visitorHasInteracted = false;
  const interactionEvents = ['pointerdown', 'keydown', 'submit'];

  function recordVisitorInteraction() {
    visitorHasInteracted = true;
  }

  function removeInteractionListeners() {
    interactionEvents.forEach(function (eventName) {
      document.removeEventListener(eventName, recordVisitorInteraction, true);
    });
  }

  interactionEvents.forEach(function (eventName) {
    document.addEventListener(eventName, recordVisitorInteraction, {
      once: true,
      capture: true
    });
  });

  // 4. For an Australian result, build the AU homepage URL and redirect.
  function redirectAustralianVisitor(countryCode) {
    if (countryCode !== 'AU' || visitorHasInteracted) return;

    const destinationUrl = new URL('https://madtea.com.au/');

    // Carry campaign tags, but do not copy unrelated or private query values.
    currentUrl.searchParams.forEach(function (value, parameterName) {
      const isCampaignTag = /^utm_(source|medium|campaign|term|content|id)$/
        .test(parameterName);
      const isAdvertisingClickId = /^(gclid|dclid|gbraid|wbraid|fbclid|msclkid|ttclid)$/
        .test(parameterName);

      if (isCampaignTag || isAdvertisingClickId) {
        destinationUrl.searchParams.append(parameterName, value);
      }
    });

    writeSessionValue('redirected', '1');
    window.location.replace(destinationUrl.href);
  }

  // 5. Reuse a valid country result for up to 30 minutes in this browser tab.
  const countryCacheDurationMs = 30 * 60 * 1000;

  try {
    const cachedCountry = JSON.parse(readSessionValue('country') || 'null');
    const now = Date.now();
    const hasValidCache = cachedCountry &&
      /^[A-Z]{2}$/.test(cachedCountry.code) &&
      cachedCountry.at <= now &&
      now - cachedCountry.at < countryCacheDurationMs;

    if (hasValidCache) {
      redirectAustralianVisitor(cachedCountry.code);
      removeInteractionListeners();
      return;
    }
  } catch {
    // Ignore invalid cached data and request a fresh country result.
  }

  // 6. Ask IPinfo for the country, allowing at most two seconds.
  if (!window.fetch || !window.AbortController) {
    removeInteractionListeners();
    return;
  }

  const lookupController = new AbortController();
  const lookupTimeoutMs = 2000;
  let lookupHasTimedOut = false;

  const timeoutId = window.setTimeout(function () {
    lookupHasTimedOut = true;
    lookupController.abort();
    removeInteractionListeners();
  }, lookupTimeoutMs);

  // IPinfo sees the browser's IP. We omit cookies and the storefront referrer.
  window.fetch('https://ipinfo.io/json', {
    signal: lookupController.signal,
    credentials: 'omit',
    referrerPolicy: 'no-referrer',
    cache: 'no-store'
  })
    .then(function (response) {
      if (!response.ok) throw new Error('Country lookup unavailable');
      return response.json();
    })
    .then(function (result) {
      if (lookupHasTimedOut || !result) return;

      const countryCode = result.country;
      const hasValidCountryCode = typeof countryCode === 'string' &&
        /^[A-Z]{2}$/.test(countryCode);

      if (!hasValidCountryCode) return;

      writeSessionValue('country', JSON.stringify({
        code: countryCode,
        at: Date.now()
      }));
      redirectAustralianVisitor(countryCode);
    })
    .catch(function () {
      // Any lookup failure leaves the visitor on the US store.
    })
    .finally(function () {
      window.clearTimeout(timeoutId);
      removeInteractionListeners();
    });
}());
